import React, { useMemo } from 'react';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import Card from './common/Card';

const CLASSIFICATION_ORDER = ['Info', 'Low', 'Moderate', 'High', 'Critical'];
const LIKELIHOOD_LEVELS = [1, 2, 3, 4, 5];  // Very Low to Very High
const IMPACT_LEVELS = [1, 2, 3, 4, 5];     // Very Low to Very High
const LEVEL_LABELS = {
  1: 'Very Low',
  2: 'Low',
  3: 'Moderate',
  4: 'High',
  5: 'Very High',
};

const GAUGE_COLORS = {
  Info: '#94a3b8',
  Low: '#38bdf8',
  Moderate: '#facc15',
  High: '#fb923c',
  Critical: '#dc2626',
};

// Map [likelihood, impact] score to risk classification
function getRiskClassification(likelihood, impact) {
  const score = likelihood * impact;
  if (score >= 15) return 'Critical';
  if (score >= 10) return 'High';
  if (score >= 5) return 'Moderate';
  if (score >= 3) return 'Low';
  return 'Info';
}

// Get cell background based on risk classification
const getRiskCellBg = (classification) => {
  const classMap = {
    'critical': 'bg-red-200 border-red-400',
    'high': 'bg-orange-200 border-orange-400',
    'moderate': 'bg-yellow-100 border-yellow-400',
    'low': 'bg-blue-100 border-blue-300',
    'info': 'bg-slate-100 border-slate-300',
  };
  return classMap[classification?.toLowerCase()] || 'bg-slate-100 border-slate-300';
};


export default function AssetRiskDashboard({ data }) {
  // Build Risk Matrix: Likelihood × Impact with assets
  const riskMatrixData = useMemo(() => {
    if (!data?.rows || data.rows.length === 0) {
      return { matrix: {}, max_assets: 0 };
    }

    // Deduplicate assets and estimate their risk position
    const uniqueAssets = {};  // asset_name -> { asset_name, likelihood, impact }
    
    data.rows.forEach((row) => {
      if (!row.asset_name) return;
      const asset_key = row.asset_name;
      
      // For each unique asset, calculate its position in the matrix
      // Impact: derive from risk_x_asset value (1-5 scale)
      const impact = Math.max(1, Math.min(5, Math.ceil(row.risk_x_asset || 1)));
      
      // Likelihood: derive from risk_x_threat value
      // risk_x_threat is 1-5 directly, so use it as likelihood
      const likelihood = Math.max(1, Math.min(5, Math.ceil(row.risk_x_threat || 1)));
      
      if (!uniqueAssets[asset_key]) {
        uniqueAssets[asset_key] = {
          asset_name: row.asset_name,
          likelihood,
          impact,
        };
      }
    });

    // Build matrix: likelihood (rows) × impact (columns)
    const matrix = {};
    LIKELIHOOD_LEVELS.forEach((L) => {
      matrix[L] = {};
      IMPACT_LEVELS.forEach((I) => {
        matrix[L][I] = [];
      });
    });

    // Distribute unique assets into matrix cells
    Object.values(uniqueAssets).forEach((asset) => {
      const L = asset.likelihood;
      const I = asset.impact;
      if (matrix[L] && matrix[L][I]) {
        matrix[L][I].push(asset.asset_name);
      }
    });

    let max_assets = 0;
    Object.values(matrix).forEach((row) => {
      Object.values(row).forEach((assets) => {
        max_assets = Math.max(max_assets, assets.length);
      });
    });

    return { matrix, max_assets: Math.max(max_assets, 1) };
  }, [data]);

  // Build doughnut chart data from unique assets
  const doughnutData = useMemo(() => {
    if (!data?.rows || data.rows.length === 0) {
      return [];
    }

    // Count unique assets by classification
    const uniqueAssets = new Set();
    const classificationCounts = {};
    CLASSIFICATION_ORDER.forEach((c) => {
      classificationCounts[c] = 0;
    });

    data.rows.forEach((row) => {
      if (!row.asset_name) return;
      const asset_key = row.asset_name;
      
      if (!uniqueAssets.has(asset_key)) {
        uniqueAssets.add(asset_key);
        const classification = row.classification || 'Info';
        classificationCounts[classification]++;
      }
    });

    const total = uniqueAssets.size;
    return CLASSIFICATION_ORDER.map((level) => {
      const count = classificationCounts[level] || 0;
      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
      return {
        label: level,
        value: count,
        percentage,
        color: GAUGE_COLORS[level],
      };
    }).filter((item) => item.value > 0);
  }, [data]);

  if (!data) {
    return (
      <Card>
        <div className="text-center py-8 text-slate-500">No risk data available</div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Risk Matrix Heatmap - Full Width */}
      <Card className="p-6 overflow-hidden flex flex-col min-w-0">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
          Risk matrix — assets by quadrant
        </h3>
        <div className="w-full min-w-0 flex-1 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 shadow-inner">
          <div className="flex items-stretch w-full h-full">
            {/* Vertical label for Likelihood */}
            <div className="flex items-center justify-center py-4 pr-2" style={{ minWidth: 28 }}>
              <span
                className="text-[10px] font-medium uppercase tracking-wider text-slate-500 whitespace-nowrap"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(-180deg)' }}
              >
                Likelihood
              </span>
            </div>

            {/* Matrix Grid */}
            <div className="flex-1 min-w-0">
              <div className="grid border border-slate-300" style={{ gridTemplateColumns: 'repeat(6, 1fr)', gridAutoRows: 'minmax(48px, auto)' }}>
                {/* Header: Impact levels */}
                <div className="flex items-center justify-center border-b border-r border-slate-300 bg-slate-200/90 p-1">
                  <span className="text-[9px] font-medium text-slate-600"></span>
                </div>
                {IMPACT_LEVELS.map((I) => (
                  <div
                    key={`impact-${I}`}
                    className="flex items-center justify-center border-b border-r border-slate-300 bg-slate-200/90 p-1 last:border-r-0"
                  >
                    <span className="text-[9px] font-medium text-slate-700">{LEVEL_LABELS[I]}</span>
                  </div>
                ))}

                {/* Likelihood rows - reversed so Very Low is at bottom */}
                {LIKELIHOOD_LEVELS.slice().reverse().map((L) => (
                  <React.Fragment key={`likelihood-${L}`}>
                    <div className="flex items-center justify-center border-b border-r border-slate-300 bg-slate-200/90 p-1">
                      <span className="text-[9px] font-medium text-slate-700">{LEVEL_LABELS[L]}</span>
                    </div>
                    {IMPACT_LEVELS.map((I) => {
                      const assets = riskMatrixData.matrix[L]?.[I] || [];
                      const classification = getRiskClassification(L, I);
                      const bgClass = getRiskCellBg(classification);
                      
                      return (
                        <div
                          key={`cell-${L}-${I}`}
                          className={`min-h-[48px] p-1 flex flex-wrap items-center justify-center gap-1 border-b border-r border-slate-300 ${bgClass} overflow-hidden`}
                        >
                          {assets.length === 0 ? (
                            <span className="text-xs opacity-50">—</span>
                          ) : (
                            assets.map((assetName) => (
                              <div
                                key={assetName}
                                className="w-5 h-5 flex-shrink-0 rounded-sm bg-white/50 border border-black/15 overflow-hidden shadow-sm relative"
                                title={assetName}
                              >
                                <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold pointer-events-none">
                                  {(assetName || '?').slice(0, 1).toUpperCase()}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>

              {/* Horizontal label for Impact */}
              <div className="flex justify-center py-1">
                <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">Impact</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Export doughnut component separately
export function AssetRiskDistribution({ doughnutData }) {
  if (!doughnutData || doughnutData.length === 0) {
    return (
      <Card className="p-5 flex flex-col min-w-0">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
          Asset risk distribution
        </h3>
        <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
          No assets assessed yet
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5 flex flex-col min-w-0">
      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
        Asset risk distribution
      </h3>
      <div className="space-y-0 flex-1">
        <ResponsiveContainer width="100%" height={230}>
          <RechartsPie>
            <Pie
              data={doughnutData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="46%"
              innerRadius={54}
              outerRadius={82}
              paddingAngle={3}
              strokeWidth={0}
              label={({ label, percentage, cx, x, y }) => (
                <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fill="#475569" fontSize={11} fontWeight={500}>
                  {`${label} ${percentage}%`}
                </text>
              )}
              labelLine={{ stroke: '#cbd5e1', strokeWidth: 1 }}
            >
              {doughnutData.map((entry) => (
                <Cell key={`cell-${entry.label}`} fill={entry.color} />
              ))}
            </Pie>
          </RechartsPie>
        </ResponsiveContainer>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center text-xs pt-1">
          {doughnutData.map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="font-medium text-slate-600">
                {item.label} <span className="text-slate-400">({item.value})</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
